<!--
	What the import will do, record by record (§6.5).

	The dry run's report says how many findings there are; this says what happens to each staged
	subscription and transaction — what it becomes, why, what it maps onto, and what Braintree said
	about it — so a batch can be debugged one customer at a time before anything is written. Any record
	can also be checked against Braintree *now*, beside the legacy store and the dry run's read.

	Its own component with its own reads: the plan is thousands of rows, paged from the server, and a
	failure here must not cost the mapping screen around it.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Badge, Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel } from '../../components';
	import {
		checkMigrationPlanItemProvider,
		getMigrationPlanItem,
		listMigrationPlan,
		type MigrationPlanPage,
		type MigrationPlanRow,
		type MigrationProviderCheck
	} from '../../AppNetwork';

	export let batchUUID: string;

	const PAGE_SIZE = 50;

	let page: MigrationPlanPage | null = null;
	let loadError: string = '';
	let loading: boolean = false;

	let kind: string = '';
	let outcome: string = '';
	let severity: string = '';
	let mismatch: boolean = false;
	let q: string = '';
	let offset: number = 0;

	/** The record opened for a closer look, and what was read for it. */
	let openKey: string | null = null;
	let openRecord: Record<string, unknown> | null = null;
	let openError: string = '';
	let check: MigrationProviderCheck | null = null;
	let checking: boolean = false;
	let checkError: string = '';

	async function load(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			page = await listMigrationPlan(batchUUID, {
				kind,
				outcome,
				severity,
				mismatch,
				q,
				limit: PAGE_SIZE,
				offset
			});
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/** A filter moved: back to the first page, since the old offset is into a different list. */
	function refilter(): void {
		offset = 0;
		void load();
	}

	/** A count chip is a shortcut for the two filters it names. */
	function narrowTo(key: string): void {
		const [chipKind, chipOutcome] = key.split(':');
		kind = chipKind;
		outcome = chipOutcome;
		refilter();
	}

	const keyOf = (row: MigrationPlanRow): string => `${row.kind}:${row.sourceID}`;

	async function toggle(row: MigrationPlanRow): Promise<void> {
		if (openKey === keyOf(row)) {
			openKey = null;
			return;
		}
		openKey = keyOf(row);
		openRecord = null;
		openError = '';
		check = null;
		checkError = '';
		try {
			openRecord = (await getMigrationPlanItem(batchUUID, row.kind, row.sourceID)).record;
		} catch (error) {
			openError = (error as Error).message;
		}
	}

	async function askBraintree(row: MigrationPlanRow): Promise<void> {
		checking = true;
		checkError = '';
		try {
			check = await checkMigrationPlanItemProvider(batchUUID, row.kind, row.sourceID);
		} catch (error) {
			checkError = (error as Error).message;
		} finally {
			checking = false;
		}
	}

	onMount(load);

	const OUTCOME_COLOR: Record<string, 'green' | 'blue' | 'purple' | 'yellow' | 'red' | 'dark'> = {
		subscription: 'green',
		grant: 'purple',
		order: 'blue',
		invoice: 'blue',
		skip: 'yellow',
		fail: 'red'
	};
	/** The same colours as solid fills, for the bar — whole literals so the JIT keeps them. */
	const OUTCOME_FILL: Record<string, string> = {
		subscription: 'bg-green-500',
		grant: 'bg-purple-500',
		order: 'bg-blue-500',
		invoice: 'bg-sky-500',
		skip: 'bg-yellow-400',
		fail: 'bg-red-500'
	};
	const OUTCOME_WORDS: Record<string, string> = {
		subscription: 'becomes a subscription',
		grant: 'becomes a grant',
		order: 'becomes an order',
		invoice: 'becomes an invoice',
		skip: 'skipped',
		fail: 'fails'
	};

	/** Any value as text a person can read, keeping what kind of value it was. */
	function show(value: unknown): string {
		if (value === null || value === undefined || value === '') return '—';
		if (typeof value === 'boolean') return value ? 'yes' : 'no';
		if (typeof value === 'object') return JSON.stringify(value);
		return String(value);
	}
	const json = (value: unknown): string => JSON.stringify(value, null, 2);

	/** Per kind, each outcome's share — the bar that says what the batch mostly is. */
	$: shape = page === null
		? []
		: (['subscription', 'transaction'] as const).map(chipKind => {
				const entries = Object.entries(page?.facets.byOutcome ?? {})
					.filter(([key]) => key.startsWith(`${chipKind}:`))
					.map(([key, count]) => ({ key, outcome: key.split(':')[1], count }))
					.sort((left, right) => right.count - left.count);
				const total = entries.reduce((sum, entry) => sum + entry.count, 0);
				return { kind: chipKind, entries, total };
			});

	$: lastPage = page === null ? 0 : Math.max(Math.ceil(page.total / PAGE_SIZE) - 1, 0);
</script>

<Panel title="What the import will do" faIcon="fa-list-check">
	{#if loadError !== ''}
		<EmptyState variant="error" title="The plan could not be loaded" description={loadError} />
	{:else if page === null}
		<EmptyState variant="loading" title="Reading the plan…" />
	{:else if page.dryRun === null}
		<EmptyState
			variant="empty"
			title="No plan yet"
			description="The plan is worked out by the dry run. Run it, and every subscription and transaction in this batch is listed here with what the import will do with it."
		/>
	{:else}
		<p class="text-default text-sm">
			From the dry run finished {new Date(page.dryRun.completedAt).toLocaleString()}. It changes only when the
			dry run is run again — mapping a product afterwards is not reflected until then.
		</p>

		<!-- The shape of the batch: what each kind of record mostly becomes. Each segment filters. -->
		{#each shape as bar (bar.kind)}
			{#if bar.total > 0}
				<div class="mt-3">
					<p class="text-xs text-default capitalize">{bar.kind}s · {bar.total}</p>
					<div class="flex h-3 w-full overflow-hidden rounded-full bg-depth" role="img" aria-label={`${bar.kind} outcomes`}>
						{#each bar.entries as entry (entry.key)}
							<button
								type="button"
								class="h-full {OUTCOME_FILL[entry.outcome] ?? 'bg-gray-400'}"
								style={`width: ${(entry.count / bar.total) * 100}%`}
								title={`${entry.count} ${OUTCOME_WORDS[entry.outcome] ?? entry.outcome}`}
								aria-label={`${entry.count} ${OUTCOME_WORDS[entry.outcome] ?? entry.outcome}`}
								on:click={() => narrowTo(entry.key)}
							/>
						{/each}
					</div>
					<div class="mt-1 flex flex-wrap gap-2">
						{#each bar.entries as entry (entry.key)}
							<button type="button" class="text-xs" on:click={() => narrowTo(entry.key)}>
								<Badge color={OUTCOME_COLOR[entry.outcome] ?? 'dark'}>{entry.count} {OUTCOME_WORDS[entry.outcome] ?? entry.outcome}</Badge>
							</button>
						{/each}
					</div>
				</div>
			{/if}
		{/each}
		<p class="text-xs text-default mt-2">
			{page.facets.bySeverity.blocking ?? 0} with a blocking finding · {page.facets.bySeverity.advisory ?? 0} with an
			advisory one · {page.facets.mismatches} subscription(s) the legacy store and Braintree disagree about
		</p>

		<!-- Filters. Each change reloads from the first page. -->
		<form class="mt-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6 items-end" on:submit|preventDefault={refilter}>
			<label class="text-xs">
				<span class="card-label">Kind</span>
				<select class="w-full rounded-lg bg-depth border-widget-color text-sm" bind:value={kind} on:change={refilter}>
					<option value="">Both</option>
					<option value="subscription">Subscriptions</option>
					<option value="transaction">Transactions</option>
				</select>
			</label>
			<label class="text-xs">
				<span class="card-label">Becomes</span>
				<select class="w-full rounded-lg bg-depth border-widget-color text-sm" bind:value={outcome} on:change={refilter}>
					<option value="">Anything</option>
					{#each Object.keys(OUTCOME_WORDS) as value (value)}
						<option {value}>{OUTCOME_WORDS[value]}</option>
					{/each}
				</select>
			</label>
			<label class="text-xs">
				<span class="card-label">Findings</span>
				<select class="w-full rounded-lg bg-depth border-widget-color text-sm" bind:value={severity} on:change={refilter}>
					<option value="">Any</option>
					<option value="blocking">Blocking</option>
					<option value="advisory">Advisory</option>
					<option value="none">None</option>
				</select>
			</label>
			<label class="text-xs flex items-center gap-2 sm:pb-2">
				<input type="checkbox" class="shrink-0" bind:checked={mismatch} on:change={refilter} />
				<span>Legacy and Braintree disagree</span>
			</label>
			<label class="text-xs lg:col-span-2">
				<span class="card-label">Search</span>
				<input
					class="w-full rounded-lg bg-depth border-widget-color text-sm"
					type="text"
					placeholder="Legacy id, customer, Braintree id, product"
					bind:value={q}
				/>
			</label>
		</form>

		{#if page.items.length === 0}
			<EmptyState variant="empty" title="Nothing matches" description="No record in the plan matches these filters." />
		{:else}
			<div class="min-w-0 overflow-x-auto mt-3">
				<Table class="dataTable">
					<TableHead>
						<TableHeadCell>Record</TableHeadCell>
						<TableHeadCell>Product</TableHeadCell>
						<TableHeadCell>Legacy → Braintree</TableHeadCell>
						<TableHeadCell>Becomes</TableHeadCell>
						<TableHeadCell>Findings</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each page.items as row (keyOf(row))}
							<TableBodyRow class="cursor-pointer" on:click={() => toggle(row)}>
								<TableBodyCell>
									<span class="capitalize text-xs text-default">{row.kind}</span>
									<span class="block font-mono text-xs">{row.sourceID}</span>
									<span class="block font-mono text-xs text-default">{row.customerKey}</span>
								</TableBodyCell>
								<TableBodyCell>
									<span class="block">{row.detail.productName ?? 'Not mapped'}</span>
									<span class="block text-xs text-default">
										{[row.detail.amount, row.detail.currency?.toUpperCase()].filter(Boolean).join(' ')}
									</span>
								</TableBodyCell>
								<TableBodyCell>
									<span class="block text-xs">{show(row.detail.legacyStatus)}</span>
									{#if row.detail.remote !== null}
										<span class="block text-xs">
											→ {row.detail.remote.status ?? (row.detail.remote.isGone ? 'not found' : 'unreadable')}
										</span>
									{/if}
									{#if row.statusMismatch === true}
										<Badge color="red">disagree</Badge>
									{/if}
								</TableBodyCell>
								<TableBodyCell>
									<Badge color={OUTCOME_COLOR[row.outcome] ?? 'dark'}>{OUTCOME_WORDS[row.outcome] ?? row.outcome}</Badge>
									{#if row.detail.projectedStatus !== null}
										<span class="text-xs">({row.detail.projectedStatus})</span>
									{/if}
									{#if row.detail.reason !== null}
										<span class="block text-xs text-default">{row.detail.reason}</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell>
									{#if row.severity !== null}
										<Badge color={row.severity === 'blocking' ? 'red' : 'yellow'}>
											{row.detail.findings.length} {row.severity}
										</Badge>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
							{#if openKey === keyOf(row)}
								<TableBodyRow>
									<TableBodyCell colspan={5}>
										<div class="space-y-3 text-sm">
											{#if row.detail.findings.length > 0}
												<ul class="space-y-1">
													{#each row.detail.findings as finding, index (index)}
														<li>
															<Badge color={finding.severity === 'blocking' ? 'red' : 'yellow'}>{finding.code}</Badge>
															{finding.message}
														</li>
													{/each}
												</ul>
											{/if}
											<p class="text-xs">
												Maps to product {show(row.detail.productID)} at price {show(row.detail.priceID)} · legacy product
												<span class="font-mono">{show(row.detail.legacyProduct)}</span> · {row.detail.refunds} refund(s) ·
												Braintree <span class="font-mono">{show(row.detail.braintreeSubscriptionID)}</span>
												<span class="font-mono">{show(row.detail.braintreeTransactionID)}</span>
											</p>
											{#if row.detail.remote?.error}
												<p class="text-xs text-red-500">At the dry run Braintree answered: {row.detail.remote.error}</p>
											{/if}

											<div>
												<Button size="xs" color="alternative" disabled={checking} on:click={() => askBraintree(row)}>
													{checking ? 'Asking Braintree…' : 'Check Braintree now'}
												</Button>
												{#if checkError !== ''}
													<p class="text-xs text-red-500 mt-1">{checkError}</p>
												{/if}
												{#if check !== null}
													{#if check.error !== null}
														<p class="text-xs text-red-500 mt-1">{check.error}</p>
													{:else}
														<Table class="dataTable mt-2">
															<TableHead>
																<TableHeadCell>Field</TableHeadCell>
																<TableHeadCell>Legacy store</TableHeadCell>
																<TableHeadCell>Dry run</TableHeadCell>
																<TableHeadCell>Braintree now</TableHeadCell>
																<TableHeadCell>Match</TableHeadCell>
															</TableHead>
															<TableBody>
																{#each check.comparisons as comparison (comparison.field)}
																	<TableBodyRow>
																		<TableBodyCell>{comparison.field}</TableBodyCell>
																		<TableBodyCell>{show(comparison.legacy)}</TableBodyCell>
																		<TableBodyCell>{show(comparison.dryRun)}</TableBodyCell>
																		<TableBodyCell>{show(comparison.provider)}</TableBodyCell>
																		<TableBodyCell>
																			{#if comparison.matches === true}
																				<Badge color="green">matches</Badge>
																			{:else if comparison.matches === false}
																				<Badge color="red">differs</Badge>
																			{/if}
																		</TableBodyCell>
																	</TableBodyRow>
																{/each}
															</TableBody>
														</Table>
													{/if}
													{#if check.remote !== null}
														<details class="mt-1">
															<summary class="cursor-pointer text-default text-xs">Braintree's answer, as mapped</summary>
															<pre class="text-xs overflow-x-auto mt-1">{json(check.remote)}</pre>
														</details>
													{/if}
												{/if}
											</div>

											{#if openError !== ''}
												<p class="text-xs text-red-500">{openError}</p>
											{:else if openRecord !== null}
												<details>
													<summary class="cursor-pointer text-default text-xs">The staged legacy record, verbatim</summary>
													<pre class="text-xs overflow-x-auto mt-1">{json(openRecord)}</pre>
												</details>
											{/if}
										</div>
									</TableBodyCell>
								</TableBodyRow>
							{/if}
						{/each}
					</TableBody>
				</Table>
			</div>
			<div class="mt-2 flex items-center gap-2 text-sm">
				<Button size="xs" color="alternative" disabled={loading || offset === 0} on:click={() => { offset = Math.max(offset - PAGE_SIZE, 0); void load(); }}>
					Previous
				</Button>
				<span class="text-default text-xs">
					{offset + 1}–{Math.min(offset + PAGE_SIZE, page.total)} of {page.total}
				</span>
				<Button size="xs" color="alternative" disabled={loading || offset / PAGE_SIZE >= lastPage} on:click={() => { offset += PAGE_SIZE; void load(); }}>
					Next
				</Button>
			</div>
		{/if}
	{/if}
</Panel>
